import { useState, useRef, useEffect } from 'react';
import { Reply, Pencil, Trash2 } from 'lucide-react';
import CommentBody from './CommentBody';
import CommentAttachments from './CommentAttachments';
import RichEditor from './RichEditor';
import formatRelative, { formatAbsolute } from '../../../utils/formatRelative';
import useAuthStore from '../../../store/authStore';
import useCardPanelStore from '../../../store/cardPanelStore';
import useBoardCan, { useHasServerAccess } from '../../../hooks/useBoardCan';
import { dialog } from '../../../store/dialogStore';
import { editKey, replyKey, loadDraft, saveDraft, saveDraftDebounced, clearDraft, isEmptyHtml } from '../../../utils/commentDrafts';

function Avatar({ name, url, size = 32 }) {
  const initials = name ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  return (
    <div
      className="rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {url ? <img src={url} alt={name} className="w-full h-full rounded-full object-cover" /> : initials}
    </div>
  );
}

export default function CommentItem({ comment, replies = [], cardId, users, allowReply = true, onImageReady }) {
  const currentUser = useAuthStore((s) => s.user);
  const { addComment, updateComment, deleteComment, setEditorDirty, unregisterEditor, isDiscarding } = useCardPanelStore();
  // Backend (boardRules.js): comentar/responder/editar/apagar o proprio
  // comentario exige so 'read' (board.comment.create, rank 1 — viewer
  // comenta). Nunca teve gate no comportamento atual (so a checagem de
  // autoria isOwn) — fallback preserva isso (sempre liberado pro autor);
  // so passa a valer quando o backend novo manda my_access de fato.
  const can = useBoardCan();
  const hasServerAccess = useHasServerAccess();
  const canComment = hasServerAccess ? can.read : true;
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);
  const isOwn = currentUser?.id === comment.user_id;

  // Quando o fechamento é intencional (salvar/responder/cancelar) o draft já
  // foi tratado; suprime o flush do unmount para não ressuscitar o rascunho.
  const suppressEditFlushRef = useRef(false);
  const suppressReplyFlushRef = useRef(false);

  // Reporta ao store "texto não enviado" para o popup de descarte.
  // Edição só conta se o conteúdo mudou; resposta conta se houver texto.
  useEffect(() => {
    const key = editKey(comment.id);
    setEditorDirty(key, editing && !isEmptyHtml(editText) && editText !== comment.content);
    return () => unregisterEditor(key);
  }, [editing, editText, comment.id, comment.content, setEditorDirty, unregisterEditor]);

  useEffect(() => {
    const key = replyKey(comment.id);
    setEditorDirty(key, replying && !isEmptyHtml(replyText));
    return () => unregisterEditor(key);
  }, [replying, replyText, comment.id, setEditorDirty, unregisterEditor]);

  const isEmpty = (s) => !s || s === '<p></p>' || s.replace(/<[^>]+>/g, '').trim() === '';

  const handleReply = async () => {
    if (isEmpty(replyText)) return;
    suppressReplyFlushRef.current = true;
    clearDraft(replyKey(comment.id));
    setReplyText('');
    setReplying(false);
    await addComment(cardId, replyText, comment.id);
  };

  const handleSaveEdit = async () => {
    suppressEditFlushRef.current = true;
    if (isEmpty(editText) || editText === comment.content) {
      clearDraft(editKey(comment.id));
      setEditing(false);
      setEditText(comment.content);
      return;
    }
    clearDraft(editKey(comment.id));
    setEditing(false);
    await updateComment(comment.id, editText);
  };

  const handleDelete = async () => {
    if (!(await dialog.confirm('Excluir atualização', 'Tem certeza que deseja excluir esta atualização?', { confirmLabel: 'Excluir', danger: true }))) return;
    await deleteComment(comment.id);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-3">
      <div className="flex items-start gap-3">
        <Avatar name={comment.user_name} url={comment.user_avatar} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary">{comment.user_name || 'Usuário'}</span>
            <span className="text-xs text-text-secondary" title={formatAbsolute(comment.created_at)}>{formatRelative(comment.created_at)}</span>
            {comment.updated_at && <span className="text-[10px] text-text-secondary/70">(editado)</span>}
            {isOwn && canComment && !editing && (
              <div className="ml-auto flex items-center gap-1 opacity-0 group-hover/c:opacity-100 transition-opacity">
                <button onClick={() => { suppressEditFlushRef.current = false; setEditing(true); setEditText(loadDraft(editKey(comment.id)) ?? comment.content); }} className="p-1 rounded hover:bg-white/10 text-text-secondary" title="Editar">
                  <Pencil className="w-3 h-3" />
                </button>
                <button onClick={handleDelete} className="p-1 rounded hover:bg-danger/10 text-text-secondary hover:text-danger" title="Excluir">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
          <div className="mt-1">
            {editing ? (
              <>
                <RichEditor value={editText} onChange={(html) => { setEditText(html); saveDraftDebounced(editKey(comment.id), html); }} onFlush={(html) => { if (isDiscarding() || suppressEditFlushRef.current) return; if (html === comment.content) clearDraft(editKey(comment.id)); else saveDraft(editKey(comment.id), html); }} users={users} autoFocus />
                <div className="mt-2 flex justify-end gap-2">
                  <button onClick={() => { suppressEditFlushRef.current = true; clearDraft(editKey(comment.id)); setEditing(false); setEditText(comment.content); }} className="text-xs text-text-secondary px-3 py-1">Cancelar</button>
                  <button onClick={handleSaveEdit} className="text-xs bg-accent text-white px-3 py-1 rounded">Salvar</button>
                </div>
              </>
            ) : (
              <>
                <CommentBody content={comment.content} />
                <CommentAttachments assets={comment.assets} onImageReady={onImageReady} />
              </>
            )}
          </div>
          {!editing && allowReply && (
            <div className="mt-2 flex items-center gap-3 text-xs text-text-secondary">
              {canComment && (
                <button onClick={() => { setReplying((v) => { if (!v) { suppressReplyFlushRef.current = false; setReplyText(loadDraft(replyKey(comment.id)) ?? ''); } return !v; }); }} className="flex items-center gap-1 hover:text-accent transition-colors">
                  <Reply className="w-3 h-3" />
                  Responder
                </button>
              )}
              {replies.length > 0 && <span>{replies.length} {replies.length === 1 ? 'resposta' : 'respostas'}</span>}
            </div>
          )}
          {replying && canComment && (
            <div className="mt-3">
              <RichEditor value={replyText} onChange={(html) => { setReplyText(html); saveDraftDebounced(replyKey(comment.id), html); }} onFlush={(html) => { if (isDiscarding() || suppressReplyFlushRef.current) return; saveDraft(replyKey(comment.id), html); }} users={users} autoFocus rows={2} placeholder="Escreva uma resposta..." />
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={() => { suppressReplyFlushRef.current = true; clearDraft(replyKey(comment.id)); setReplying(false); setReplyText(''); }} className="text-xs text-text-secondary px-3 py-1">Cancelar</button>
                <button onClick={handleReply} disabled={!replyText.trim()} className="text-xs bg-accent text-white px-3 py-1 rounded disabled:opacity-40">Responder</button>
              </div>
            </div>
          )}
          {replies.length > 0 && (
            <div className="mt-4 pl-4 border-l-2 border-border/50 space-y-3">
              {replies.map((r) => (
                <div key={r.id} className="group/c flex items-start gap-2">
                  <Avatar name={r.user_name} url={r.user_avatar} size={24} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-text-primary">{r.user_name || 'Usuário'}</span>
                      <span className="text-[10px] text-text-secondary" title={formatAbsolute(r.created_at)}>{formatRelative(r.created_at)}</span>
                      {r.user_id === currentUser?.id && canComment && (
                        <button onClick={async () => { if (await dialog.confirm('Excluir resposta', 'Tem certeza?', { confirmLabel: 'Excluir', danger: true })) deleteComment(r.id); }} className="ml-auto p-0.5 rounded hover:bg-danger/10 text-text-secondary hover:text-danger opacity-0 group-hover/c:opacity-100 transition-opacity">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <CommentBody content={r.content} />
                    <CommentAttachments assets={r.assets} onImageReady={onImageReady} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
